import React from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import { Download, Clock, Printer } from 'lucide-react';
import { EXPIRED_IMAGE_TEXT } from '../utils/attachmentExpiry';
import { useCloseWatcher } from '../hooks/useCloseWatcher';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

export interface LightboxSlide {
  src: string;
  alt: string;
  downloadUrl: string;
  downloadFilename: string;
  /**
   * 보관 기간이 지난 첨부. 원본 파일이 삭제돼 src가 404이므로 이미지 대신 안내를 그리고
   * 다운로드 버튼을 숨긴다. 호출부가 slides 배열에서 만료 항목을 **빼면 안 된다** —
   * AttachmentDrawer의 인덱스가 flat items 기준이라 어긋난다.
   */
  expired?: boolean;
}

interface ImageLightboxProps {
  open: boolean;
  slides: LightboxSlide[];
  index: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
  onDownload: (slide: LightboxSlide) => void;
  /** 주면 툴바에 인쇄 버튼이 생긴다(만료 슬라이드에서는 다운로드와 함께 숨는다) */
  onPrint?: (slide: LightboxSlide) => void;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  slides,
  index,
  onClose,
  onIndexChange,
  onDownload,
  onPrint,
}) => {
  // 모바일 뒤로가기는 사진 뷰어만 닫는다. 아래 early return보다 앞이어야 한다(훅 순서)
  useCloseWatcher(open && slides.length > 0, onClose);

  if (!open || slides.length === 0) return null;

  const safeIndex = Math.min(Math.max(index, 0), slides.length - 1);
  const isCurrentExpired = !!slides[safeIndex]?.expired;

  const downloadButton = (
    <button
      key="download"
      type="button"
      className="yarl__button"
      title="다운로드"
      aria-label="다운로드"
      onClick={() => {
        const target = slides[safeIndex];
        if (target) onDownload(target);
      }}
    >
      <Download size={24} />
    </button>
  );

  const printButton = onPrint && (
    <button
      key="print"
      type="button"
      className="yarl__button"
      title="인쇄"
      aria-label="인쇄"
      onClick={() => {
        const target = slides[safeIndex];
        if (target) onPrint(target);
      }}
    >
      <Printer size={24} />
    </button>
  );

  return (
    <Lightbox
      open={open}
      close={onClose}
      slides={slides}
      index={safeIndex}
      on={{
        view: ({ index: nextIndex }) => onIndexChange?.(nextIndex),
      }}
      plugins={[Zoom, Counter]}
      zoom={{
        maxZoomPixelRatio: 4,
        scrollToZoom: true,
        doubleTapDelay: 300,
        doubleClickDelay: 300,
      }}
      carousel={{ finite: slides.length <= 1 }}
      controller={{ closeOnBackdropClick: true }}
      render={{
        // 만료 슬라이드는 깨진 이미지 대신 안내를 그린다. undefined를 돌려주면 기본 렌더로 넘어간다.
        slide: ({ slide }) => (slide as LightboxSlide).expired ? (
          <div className="flex flex-col items-center gap-3 px-8 text-center text-white/80">
            <Clock size={40} className="opacity-70" />
            <p className="text-sm">{EXPIRED_IMAGE_TEXT}</p>
          </div>
        ) : undefined,
      }}
      toolbar={{
        buttons: isCurrentExpired
          ? ['close']
          : [...(printButton ? [printButton] : []), downloadButton, 'close'],
      }}
      counter={{ container: { style: { top: 'unset', bottom: 0 } } }}
    />
  );
};
